import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { listClients } from '../lib/api';
import { useAuth } from '../lib/auth';
import type { Client, PageId } from '../types';
import { Icon, T, sharedStyles } from './Shared';

// NavId is the subset of PageIds that are navigable WITHIN a client dashboard.
// 'users' is a top-level admin route, so it's not in this list.
export type NavId = 'dashboard' | 'scan' | 'analysis' | 'heatmap' | 'competitors' | 'citations' | 'settings';

interface SidebarProps {
  active: NavId;
  onNav: (id: NavId) => void;
  client: Client;
  // When viewing as a client (share link or client login), hide admin chrome.
  readOnly?: boolean;
  // Filter visible nav items. If undefined, show all (back-compat for share link view).
  allowedPages?: PageId[];
  userInitial?: string;
  userName?: string;
  userPlan?: string;
  onLogout?: () => void;
}

// Display order + labels. Internal `id` keys are unchanged ('scan'/'analysis')
// so existing stored permissions keep working — only the user-facing label
// changed: AI Scan → AI Visibility, AI Analysis → AI Insight.
// `comingSoon` shows a muted pill and routes to a placeholder screen.
// Order: Competitors sits above AI Insight, and AI Insight (the optimization
// recommendation) is the last/culminating item.
const ALL_NAV_ITEMS: { id: NavId; label: string; icon: 'dashboard' | 'scan' | 'zap' | 'map' | 'competitor' | 'settings'; comingSoon?: boolean }[] = [
  { id: 'dashboard',   label: 'Dashboard',     icon: 'dashboard' },
  { id: 'heatmap',     label: 'Heatmap',       icon: 'map' },
  { id: 'scan',        label: 'AI Visibility', icon: 'scan' },
  { id: 'competitors', label: 'Competitors',   icon: 'competitor' },
  { id: 'analysis',    label: 'AI Insight',    icon: 'zap' },
];

export function Sidebar({
  active, onNav, client, readOnly = false, allowedPages,
  userInitial = 'J', userName = 'John Smith', userPlan = 'Pro Plan',
  onLogout,
}: SidebarProps) {
  const visibleNav = allowedPages
    ? ALL_NAV_ITEMS.filter(item => allowedPages.includes(item.id))
    : ALL_NAV_ITEMS;

  const showSettings = !readOnly && (allowedPages ? allowedPages.includes('settings') : true);

  return (
    <div style={{
      // height:100% so it fits inside the AdminClientView flex column (which
      // already reserves space for the NavHeader). Using 100vh here double-
      // counted that header and pushed the bottom (Settings + user) off-screen.
      width: 220, minWidth: 220, height: '100%', minHeight: 0,
      background: '#f8f9fa',
      borderRight: '1px solid #dadce0',
      display: 'flex', flexDirection: 'column',
      overflow: 'hidden',
    }}>
      {/* Logo */}
      <div style={{ padding: '20px 20px 16px', borderBottom: `1px solid ${T.border}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            background: '#fff', borderRadius: 8, padding: '5px 7px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <img src="/assets/gbi-logo.png" alt="GBI" style={{ height: 32, width: 'auto', display: 'block' }} />
          </div>
          <div>
            <div style={{
              fontFamily: sharedStyles.fontDisplay, fontWeight: 700, fontSize: 11,
              background: T.brandGradient, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
              lineHeight: 1.2, whiteSpace: 'nowrap',
            }}>AI Intelligence</div>
            <div style={{ fontSize: 9, color: T.fgMuted, letterSpacing: '0.04em', marginTop: 1 }}>by YEA</div>
          </div>
        </div>
      </div>

      {/* Business selector — also the location switcher: same client group,
          so switching locations lives right here. */}
      <div style={{ padding: '14px 14px 10px' }}>
        <BusinessSwitcher client={client} readOnly={readOnly} />
      </div>

      {/* Nav — scrolls internally if it ever overflows so the bottom
          (Settings + user) stays pinned and visible. */}
      <nav style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 2 }}>
        {visibleNav.map(item => {
          const isActive = active === item.id;
          return (
            <button key={item.id} onClick={() => onNav(item.id)} style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '10px 12px', borderRadius: 8,
              background: isActive ? 'rgba(18,181,196,0.1)' : 'transparent',
              border: isActive ? '1px solid rgba(18,181,196,0.25)' : '1px solid transparent',
              color: isActive ? '#12B5C4' : '#5f6368',
              cursor: 'pointer', width: '100%', textAlign: 'left',
              fontFamily: sharedStyles.fontBody, fontSize: 13, fontWeight: isActive ? 600 : 400,
              transition: 'all 150ms',
            }}>
              <Icon name={item.icon} size={16} color={isActive ? T.cyan : T.fgMuted} />
              {item.label}
              {item.comingSoon && (
                <span style={{
                  marginLeft: 'auto', fontSize: 8, fontWeight: 700,
                  letterSpacing: '0.06em', textTransform: 'uppercase',
                  color: T.amber, background: 'rgba(245,160,32,0.12)',
                  border: `1px solid ${T.amber}`,
                  padding: '1px 5px', borderRadius: 4,
                }}>soon</span>
              )}
              {isActive && !item.comingSoon && <span style={{ marginLeft: 'auto', width: 5, height: 5, borderRadius: '50%', background: T.cyan }} />}
            </button>
          );
        })}
      </nav>

      {/* Bottom */}
      <div style={{ padding: '12px 10px', borderTop: `1px solid ${T.border}` }}>
        {showSettings && (
          <button onClick={() => onNav('settings')} style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '10px 12px', borderRadius: 8,
            background: active === 'settings' ? 'rgba(34,211,238,0.1)' : 'transparent',
            border: '1px solid transparent',
            color: T.fgSecondary, cursor: 'pointer', width: '100%',
            fontFamily: sharedStyles.fontBody, fontSize: 13,
          }}>
            <Icon name="settings" size={16} color={T.fgMuted} />
            Settings
          </button>
        )}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '10px 12px', marginTop: 4,
        }}>
          <div style={{
            width: 30, height: 30, borderRadius: '50%',
            background: 'linear-gradient(135deg, #22d3ee, #4ade80)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 12, fontWeight: 700, color: T.bgVoid,
          }}>{userInitial}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: T.fgPrimary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{userName}</div>
            <div style={{ fontSize: 10, color: T.fgMuted }}>{userPlan}</div>
          </div>
          {onLogout && (
            <button onClick={onLogout} title="Sign out" style={{
              background: 'transparent', border: 'none', cursor: 'pointer',
              padding: 4, borderRadius: 4, color: T.fgMuted,
              display: 'flex', alignItems: 'center',
            }}>
              <Icon name="settings" size={14} color={T.fgMuted} />
            </button>
          )}
        </div>
        {onLogout && (
          <button onClick={onLogout} style={{
            marginTop: 4, width: '100%',
            background: 'transparent', border: `1px solid ${T.border}`,
            borderRadius: 6, padding: '6px 10px', cursor: 'pointer',
            fontSize: 11, color: T.fgSecondary,
          }}>
            Sign out
          </button>
        )}
      </div>
    </div>
  );
}

// The "Active Business" card, doubling as the location switcher. One client
// can have several locations (each its own clients row, same business group);
// switching between them belongs right here. Read-only viewers (public share)
// get the static card with no switcher. Category is intentionally NOT shown —
// every location of a business shares the same trade; only the area differs.
function BusinessSwitcher({ client, readOnly: _readOnly }: { client: Client; readOnly: boolean }) {
  const navigate = useNavigate();
  // The switcher is interactive for any authenticated session (admin OR a
  // client login viewing their own group). Only the unauthenticated public
  // share view gets a static card — it can't navigate into /admin.
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [clients, setClients] = useState<Client[] | null>(null);
  const ref = useRef<HTMLDivElement | null>(null);

  // Lazy-load the client list the first time the switcher opens.
  useEffect(() => {
    if (!open || clients !== null) return;
    listClients().then(setClients).catch(() => setClients([]));
  }, [open, clients]);

  // Click outside closes the menu.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  // The business head is this row if it IS the head, else its parent.
  const headId = client.parentClientId ?? client.id;
  const siblings = (clients ?? [])
    .filter(c => c.id === headId || c.parentClientId === headId)
    .sort((a, b) => (a.id === headId ? -1 : b.id === headId ? 1 : a.name.localeCompare(b.name)));
  const locText = (c: Client) => c.locationLabel?.trim() || c.location;
  const multi = siblings.length > 1;

  const card = (
    <div style={{
      background: '#fff', border: '1px solid #dadce0',
      borderRadius: 8, padding: '10px 12px',
      cursor: user ? 'pointer' : 'default',
      display: 'flex', alignItems: 'center', gap: 8,
    }}>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 10, color: T.fgMuted, marginBottom: 2, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Active Business</div>
        <div style={{ fontSize: 13, fontWeight: 600, color: T.fgPrimary, fontFamily: sharedStyles.fontDisplay, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{client.name}</div>
        <div style={{ fontSize: 11, color: T.fgMuted, marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          📍 {client.locationLabel?.trim() || client.location}
        </div>
      </div>
      {user && (
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
          {multi && (
            <span style={{
              fontSize: 9, fontWeight: 700, color: T.cyan,
              background: 'rgba(18,181,196,0.10)', border: `1px solid ${T.cyan}`,
              borderRadius: 8, padding: '1px 6px',
            }}>📍 {siblings.length}</span>
          )}
          <span style={{ fontSize: 9, color: T.fgMuted }}>▼</span>
        </span>
      )}
    </div>
  );

  if (!user) return card;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div onClick={() => setOpen(o => !o)}>{card}</div>
      {open && (
        <div style={{
          position: 'absolute', top: '108%', left: 0, right: 0,
          background: '#fff', border: `1px solid ${T.border}`,
          borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          zIndex: 60, padding: 6, maxHeight: 320, overflowY: 'auto',
        }}>
          {clients === null && <div style={{ padding: 10, fontSize: 12, color: T.fgMuted }}>Loading…</div>}
          {clients !== null && siblings.length === 0 && (
            <div style={{ padding: 10, fontSize: 12, color: T.fgMuted, fontStyle: 'italic' }}>No locations.</div>
          )}
          {multi && (
            <div style={{
              fontSize: 9, fontWeight: 700, letterSpacing: '0.08em',
              textTransform: 'uppercase', color: T.cyan, padding: '6px 10px 4px',
            }}>{client.name} · {siblings.length} locations</div>
          )}
          {siblings.map(c => {
            const cur = c.id === client.id;
            return (
              <div key={c.id}
                onClick={() => { setOpen(false); if (!cur) navigate(`/admin/clients/${c.id}`); }}
                style={{
                  padding: '8px 10px', borderRadius: 6, cursor: 'pointer',
                  background: cur ? 'rgba(18,181,196,0.10)' : 'transparent',
                }}
                onMouseEnter={e => { if (!cur) e.currentTarget.style.background = T.bgSubtle; }}
                onMouseLeave={e => { if (!cur) e.currentTarget.style.background = 'transparent'; }}
              >
                <div style={{ fontSize: 12, fontWeight: 600, color: T.fgPrimary }}>
                  {locText(c)}
                  {cur && <span style={{ fontSize: 9, color: T.cyan, marginLeft: 6 }}>● current</span>}
                </div>
                <div style={{ fontSize: 10, color: T.fgMuted, marginTop: 1 }}>📍 {c.location}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
